import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { ArrowLeft, Download, TrendingUp, TrendingDown, Wallet, Loader2, RefreshCw, FileText } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const inr = (n) => `₹${Math.round(Number(n || 0)).toLocaleString("en-IN")}`;
const fmtDate = (iso) => { if (!iso) return "—"; try { return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); } catch { return iso; } };

const STATUS_TONE = {
  awaiting_payment: "text-[#D8B23E] border-[#D8B23E]/40",
  pending: "text-[#B87333] border-[#B87333]/40",
  paid: "text-[#2FB999] border-[#2FB999]/40",
};

export default function SupplierSettlement() {
  const { user, api } = useAuth();
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({});
  const [pct, setPct] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [tab, setTab] = useState("earnings");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, inv] = await Promise.all([
        api.get("/settlement/mine"),
        api.get("/settlement/invoices"),
      ]);
      setRows(s.data.rows || []); setTotals(s.data.totals || {}); setPct(s.data.commission_pct);
      setInvoices(inv.data || []);
    } catch (e) { toast.error("Could not load settlement"); }
    finally { setLoading(false); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-[#070707] text-[#F5F5F0] px-6 md:px-12 py-16">
      <Toaster position="bottom-right" theme="dark" richColors />
      <div className="max-w-[1400px] mx-auto">
        <Link to="/account" data-testid="settlement-back" className="inline-flex items-center gap-2 text-[#8A8A82] hover:text-[#B87333] text-xs uppercase tracking-[0.2em] font-grotesk mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to Account
        </Link>

        <div className="flex flex-wrap items-end justify-between gap-4 mb-10 border-b border-white/10 pb-6">
          <div>
            <div className="overline text-[#B87333] mb-2">Settlement Desk</div>
            <h1 className="font-serif text-4xl md:text-5xl">Net Earnings</h1>
            <p className="font-body text-sm text-[#8A8A82] mt-2">
              Gross sales, Vinci commission, TDS and your net payable — auto-refreshed after every buyer payment.
            </p>
          </div>
          <button data-testid="settlement-refresh" onClick={load} className="rounded-full border border-white/20 px-4 py-2 text-xs uppercase tracking-[0.2em] hover:border-[#B87333] flex items-center gap-2">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <SCard label="Gross Sales" value={inr(totals.gross)} icon={<TrendingUp className="w-4 h-4 text-[#F5F5F0]" />} testid="stat-gross" />
          <SCard label={`Vinci Commission · ${pct?.toFixed?.(1) ?? pct}%`} value={inr(totals.commission)} icon={<TrendingDown className="w-4 h-4 text-[#D8B23E]" />} tone="text-[#D8B23E]" testid="stat-commission" />
          <SCard label="TDS · 1%" value={inr(totals.tds)} icon={<TrendingDown className="w-4 h-4 text-[#8A8A82]" />} testid="stat-tds" />
          <SCard label="Net Payable" value={inr(totals.net_payable)} subtitle={`Cleared ${inr(totals.net_paid)} · Pending ${inr(totals.net_pending)}`} icon={<Wallet className="w-4 h-4 text-[#2FB999]" />} tone="text-[#2FB999]" testid="stat-net" />
        </div>

        <div className="flex gap-2 mb-6">
          {[["earnings", "Earnings"], ["invoices", "Invoice Repository"]].map(([k, label]) => (
            <button key={k} data-testid={`sub-${k}`} onClick={() => setTab(k)}
              className={`rounded-full px-5 py-2 text-xs uppercase tracking-[0.2em] font-grotesk transition-colors ${tab === k ? "bg-[#F5F5F0] text-black" : "border border-white/20 text-[#C9C9C2] hover:border-[#B87333]"}`}>{label}</button>
          ))}
        </div>

        {loading ? (
          <div className="p-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#B87333]" /></div>
        ) : tab === "earnings" ? (
          <div className="border border-white/10 rounded-2xl overflow-hidden">
            <div className="hidden md:grid grid-cols-[120px_140px_1fr_120px_120px_120px_120px] gap-3 px-6 py-4 border-b border-white/10 bg-white/[0.02] text-[10px] uppercase tracking-[0.2em] text-[#8A8A82] font-grotesk">
              <div>Date</div><div>PO</div><div>Buyer / State</div><div className="text-right">Gross</div><div className="text-right">Commission</div><div className="text-right">Net</div><div>Payout</div>
            </div>
            {rows.length === 0 ? <div className="p-16 text-center text-[#8A8A82] font-body text-sm">No earnings yet.</div> :
              rows.map((r, i) => (
                <div key={i} data-testid={`earn-row-${i}`} className="grid grid-cols-1 md:grid-cols-[120px_140px_1fr_120px_120px_120px_120px] gap-3 px-6 py-4 border-b border-white/5 hover:bg-white/[0.02]">
                  <div className="text-xs text-[#C9C9C2]">{fmtDate(r.date)}</div>
                  <div className="text-xs text-[#F5F5F0] font-mono">{r.ref}</div>
                  <div className="text-xs text-[#C9C9C2]">{r.buyer_email} · {r.shipping_state || "—"}</div>
                  <div className="text-xs text-right">{inr(r.gross)}</div>
                  <div className="text-xs text-right text-[#D8B23E]">-{inr(r.commission)}</div>
                  <div className="text-xs text-right text-[#2FB999]">{inr(r.net_payable)}</div>
                  <div><span className={`inline-block rounded-full border px-3 py-0.5 text-[10px] uppercase tracking-[0.15em] font-grotesk ${STATUS_TONE[r.payout_status] || "text-[#8A8A82] border-white/20"}`}>{(r.payout_status || "—").replace(/_/g, " ")}</span></div>
                </div>
              ))}
          </div>
        ) : (
          <div className="border border-white/10 rounded-2xl overflow-hidden">
            <div className="hidden md:grid grid-cols-[140px_120px_1fr_100px_120px_140px] gap-3 px-6 py-4 border-b border-white/10 bg-white/[0.02] text-[10px] uppercase tracking-[0.2em] text-[#8A8A82] font-grotesk">
              <div>PO Number</div><div>Date</div><div>Buyer / State</div><div>Items</div><div className="text-right">Total</div><div>Actions</div>
            </div>
            {invoices.length === 0 ? <div className="p-16 text-center text-[#8A8A82] font-body text-sm">No purchase orders yet.</div> :
              invoices.map((inv) => (
                <div key={inv.order_id} data-testid={`inv-row-${inv.order_id}`} className="grid grid-cols-1 md:grid-cols-[140px_120px_1fr_100px_120px_140px] gap-3 px-6 py-4 border-b border-white/5 hover:bg-white/[0.02]">
                  <div className="text-xs text-[#F5F5F0] font-mono">{inv.po_number}</div>
                  <div className="text-xs text-[#C9C9C2]">{fmtDate(inv.date)}</div>
                  <div className="text-xs text-[#C9C9C2]">{inv.buyer_email} · {inv.shipping_state || "—"}</div>
                  <div className="text-xs text-[#C9C9C2]">{inv.items_count}</div>
                  <div className="text-xs text-right">{inr(inv.total)}</div>
                  <div>
                    <a href={inv.download_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-[#B87333] hover:brightness-125">
                      <Download className="w-3 h-3" /> PDF
                    </a>
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  );
}

function SCard({ label, value, subtitle, icon, tone, testid }) {
  return (
    <div data-testid={testid} className="border border-white/10 rounded-2xl p-6 bg-white/[0.02]">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#8A8A82] font-grotesk mb-3">{icon}<span>{label}</span></div>
      <div className={`font-serif text-3xl ${tone || ""}`}>{value}</div>
      {subtitle ? <div className="text-[10px] text-[#8A8A82] mt-2 font-body">{subtitle}</div> : null}
    </div>
  );
}
